import React, { useEffect, useState, useCallback } from "react";
import styles from "./AdminVouchers.module.css";
import { getAccessToken } from "../../utils/authStorage";

// Use environment variable for the base URL
const ENV_API_URL =
    import.meta.env.VITE_API_URL || "https://pixelmoonstore.in/api/v1";
// Ensure we have the base API URL without version
const BASE_API_URL = ENV_API_URL.replace(/\/v1\/?$/, "");

async function api(path, method = "GET", body) {
    const token = getAccessToken();
    const headers = { "Content-Type": "application/json" };
    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    const opts = {
        method,
        headers,
        credentials: "include",
    };
    if (body) opts.body = JSON.stringify(body);

    const res = await fetch(`${BASE_API_URL}/v1/vouchers${path}`, opts);
    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "API request failed");
    }
    return res.json();
}

export default function AdminVouchers() {
    const [list, setList] = useState([]);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterType, setFilterType] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await api("", "GET");
            setList(res.vouchers || []);
        } catch (e) {
            setError("Failed to load vouchers. Please try again.");
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const save = useCallback(
        async (form) => {
            setLoading(true);
            setError(null);
            try {
                if (form._id) {
                    // Update
                    await api(`/${form._id}`, "PUT", form);
                    setSuccess("Voucher updated successfully!");
                } else {
                    // Create
                    await api("/create-custom", "POST", form);
                    setSuccess("Voucher created successfully!");
                }
                setEditing(null);
                await load();
                setTimeout(() => setSuccess(null), 3000);
            } catch (e) {
                setError(e.message || "Failed to save the voucher.");
                console.error(e);
            } finally {
                setLoading(false);
            }
        },
        [load],
    );

    const deleteVoucher = useCallback(
        async (id, name) => {
            if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;

            setLoading(true);
            setError(null);
            try {
                await api(`/${id}`, "DELETE");
                setSuccess("Voucher deleted successfully!");
                await load();
                setTimeout(() => setSuccess(null), 3000);
            } catch (e) {
                setError(e.message || "Failed to delete the voucher.");
                console.error(e);
            } finally {
                setLoading(false);
            }
        },
        [load],
    );

    // Filter vouchers based on search and type filter
    const filteredList = list.filter((voucher) => {
        const matchesSearch =
            voucher.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            voucher.code.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesType = !filterType || voucher.type === filterType;
        return matchesSearch && matchesType;
    });

    const types = [...new Set(list.map((a) => a.type))].sort();

    return (
        <div className={styles.container}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.titleSection}>
                    <h1 className={styles.title}>Voucher Management</h1>
                    <p className={styles.subtitle}>
                        Add, edit, and manage vouchers (3rd party, custom, etc.)
                    </p>
                </div>
                <button
                    className={styles.addButton}
                    onClick={() => setEditing({ type: "3rdparty" })}
                    disabled={loading}
                >
                    <span className={styles.icon}>+</span> Add New Voucher
                </button>
            </div>

            {/* Alert Messages */}
            {error && (
                <div className={`${styles.alert} ${styles.alertError}`}>{error}</div>
            )}
            {success && (
                <div className={`${styles.alert} ${styles.alertSuccess}`}>
                    {success}
                </div>
            )}

            {/* Search and Filter */}
            <div className={styles.filterSection}>
                <div className={styles.searchBox}>
                    <input
                        type="text"
                        placeholder="Search by name or code..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={styles.searchInput}
                    />
                </div>
                <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className={styles.filterSelect}
                >
                    <option value="">All Types</option>
                    {types.map((type) => (
                        <option key={type} value={type}>
                            {type}
                        </option>
                    ))}
                </select>
            </div>

            {/* Content */}
            <div className={styles.content}>
                {loading && (
                    <div className={styles.loadingState}>
                        <div className={styles.spinner}></div>
                        <p>Loading vouchers...</p>
                    </div>
                )}

                {!loading && filteredList.length === 0 && (
                    <div className={styles.emptyState}>
                        <p className={styles.emptyIcon}>🎟️</p>
                        <p className={styles.emptyText}>
                            {list.length === 0
                                ? "No vouchers yet. Create your first one!"
                                : "No vouchers match your search."}
                        </p>
                    </div>
                )}

                {!loading && filteredList.length > 0 && (
                    <div className={styles.accountsGrid}>
                        {filteredList.map((voucher) => (
                            <VoucherCard
                                key={voucher._id}
                                voucher={voucher}
                                onEdit={() => setEditing(voucher)}
                                onDelete={() => deleteVoucher(voucher._id, voucher.name)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Editor Modal */}
            {editing && (
                <VoucherEditor
                    form={editing}
                    onClose={() => setEditing(null)}
                    onSave={save}
                    isLoading={loading}
                />
            )}
        </div>
    );
}

// Voucher Card Component
function VoucherCard({ voucher, onEdit, onDelete }) {
    return (
        <div className={styles.accountCard}>
            <div className={styles.cardHeader}>
                <h3 className={styles.cardTitle}>{voucher.name}</h3>
                <span className={styles.badge}>{voucher.status || "active"}</span>
            </div>

            {voucher.image && (
                <div className={styles.cardImage}>
                    <img src={voucher.image} alt={voucher.name} />
                </div>
            )}

            <div className={styles.cardContent}>
                <p className={styles.cardGame}>
                    <strong>Type:</strong> {voucher.type}
                </p>
                <p className={styles.cardCategory}>
                    <strong>Code:</strong> {voucher.code}
                </p>
                <p className={styles.cardPrice}>
                    <strong>Price:</strong> ₹{voucher.price}
                </p>
                <p className={styles.cardPrice}>
                    <strong>Value:</strong> {voucher.denomination}
                </p>
                {voucher.description && (
                    <p className={styles.cardDescription}>
                        {voucher.description.substring(0, 80)}...
                    </p>
                )}
            </div>

            <div className={styles.cardActions}>
                <button className={`${styles.btn} ${styles.btnEdit}`} onClick={onEdit}>
                    Edit
                </button>
                <button
                    className={`${styles.btn} ${styles.btnDelete}`}
                    onClick={onDelete}
                >
                    Delete
                </button>
            </div>
        </div>
    );
}

// Voucher Editor Modal Component
function VoucherEditor({ form, onClose, onSave, isLoading }) {
    const [state, setState] = useState({ ...form });
    const [validationErrors, setValidationErrors] = useState({});

    const setField = (k, v) => {
        setState((s) => ({ ...s, [k]: v }));
        if (validationErrors[k]) {
            setValidationErrors((e) => ({ ...e, [k]: null }));
        }
    };

    const validate = () => {
        const errors = {};
        if (!state.name?.trim()) errors.name = "Name is required";
        if (!state.type) errors.type = "Type is required";
        if (!state.price || state.price <= 0)
            errors.price = "Valid price is required";
        if (!state.denomination || state.denomination <= 0)
            errors.denomination = "Valid denomination is required";

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSave = () => {
        if (validate()) {
            onSave(state);
        }
    };

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>
                        {state._id ? "Edit Voucher" : "Create New Voucher"}
                    </h2>
                    <button className={styles.closeButton} onClick={onClose}>
                        ×
                    </button>
                </div>

                <div className={styles.modalBody}>
                    <div className={styles.formGrid}>
                        {/* Left Column */}
                        <div className={styles.formColumn}>
                            <FormField
                                label="Voucher Name *"
                                value={state.name || ""}
                                onChange={(e) => setField("name", e.target.value)}
                                error={validationErrors.name}
                                placeholder="e.g., Amazon $10 Gift Card"
                            />

                            <div className={styles.formField}>
                                <label className={styles.formLabel}>Type *</label>
                                <select
                                    className={styles.formInput}
                                    value={state.type || "3rdparty"}
                                    onChange={(e) => setField("type", e.target.value)}
                                >
                                    <option value="3rdparty">3rd Party (e.g. Amazon, Xbox)</option>
                                    <option value="manual">Manual Code</option>
                                    <option value="custom">Custom Internal</option>
                                </select>
                            </div>

                            <FormField
                                label="Code (Optional)"
                                value={state.code || ""}
                                onChange={(e) => setField("code", e.target.value)}
                                placeholder="Leave empty to auto-generate"
                            />

                            <FormField
                                label="Price (INR) *"
                                type="number"
                                value={state.price || ""}
                                onChange={(e) => setField("price", Number(e.target.value))}
                                error={validationErrors.price}
                                placeholder="Your selling price"
                            />

                            <FormField
                                label="Denomination/Value *"
                                type="number"
                                value={state.denomination || ""}
                                onChange={(e) => setField("denomination", Number(e.target.value))}
                                error={validationErrors.denomination}
                                placeholder="Face value of the voucher"
                            />


                        </div>

                        {/* Right Column */}
                        <div className={styles.formColumn}>
                            <FormField
                                label="Image URL"
                                value={state.image || ""}
                                onChange={(e) => setField("image", e.target.value)}
                                placeholder="https://example.com/image.jpg"
                            />

                            <FormField
                                label="Description"
                                value={state.description || ""}
                                onChange={(e) => setField("description", e.target.value)}
                                placeholder="Description of the voucher"
                                isTextarea
                                rows={4}
                            />

                            {state._id && (
                                <div className={styles.formField}>
                                    <label className={styles.formLabel}>Status</label>
                                    <select
                                        className={styles.formInput}
                                        value={state.status || "active"}
                                        onChange={(e) => setField("status", e.target.value)}
                                    >
                                        <option value="active">Active</option>
                                        <option value="redeemed">Redeemed</option>
                                        <option value="expired">Expired</option>
                                        <option value="archived">Archived</option>
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className={styles.modalFooter}>
                    <button
                        className={`${styles.btn} ${styles.btnCancel}`}
                        onClick={onClose}
                        disabled={isLoading}
                    >
                        Cancel
                    </button>
                    <button
                        className={`${styles.btn} ${styles.btnSave}`}
                        onClick={handleSave}
                        disabled={isLoading}
                    >
                        {isLoading ? "Saving..." : "Save Voucher"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Reusable Form Field Component
function FormField({
    label,
    value,
    onChange,
    error,
    placeholder,
    type = "text",
    isTextarea = false,
    rows = 1,
}) {
    return (
        <div className={styles.formField}>
            <label className={styles.formLabel}>{label}</label>
            {isTextarea ? (
                <textarea
                    className={`${styles.formInput} ${error ? styles.inputError : ""}`}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    rows={rows}
                />
            ) : (
                <input
                    type={type}
                    className={`${styles.formInput} ${error ? styles.inputError : ""}`}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                />
            )}
            {error && <span className={styles.fieldError}>{error}</span>}
        </div>
    );
}
